import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowDown, ArrowUp, Award, Ban, Check, CircleAlert, FileText, Link2,
  Palette, Plus, Star, Trash2,
} from 'lucide-react';
import {
  getCourseCertificates,
  generateCourseCertificate,
  getCertificateShareLink,
  getCertificateTemplates,
  getCourseCertificateSettings,
  updateCourseCertificateSettings,
  getCourseCertificateSample,
  revokeCertificate,
  reissueCertificate,
  certificateAssetUrl,
  publicCertificatePdfUrl,
} from '../../api/certificates';
import { unwrapList, unwrapMeta } from '../../utils/api';
import { formatDate, formatNumber, formatPercent } from '../../utils/datetime';
import { usePagination } from '../../hooks/usePagination';
import { usePermissions } from '../../hooks/usePermissions';
import { useCourseTitle } from '../../hooks/useCourseTitle';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import Avatar from '../../components/Avatar/Avatar';
import PageHead from '../../components/PageHead/PageHead';
import Pagination from '../../components/Pagination/Pagination';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import TableCard from '../../components/TableCard/TableCard';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import MetricCard from '../../components/MetricCard/MetricCard';
import Modal from '../../components/Modal/Modal';
import { useI18n } from '../../i18n/I18nContext';
import './CourseCertificates.css';

const MAX_AUTHORS = 3;
// More rows than this and the table caps its height, so its head stays in view while it scrolls.
const TALL_ROWS = 12;

// The public page that checks a certificate lives on the marketing site.
const PUBLIC_SITE = (process.env.REACT_APP_PUBLIC_SITE_URL || 'https://darsly.uz').replace(/\/+$/, '');
const verifyUrl = (code) => `${PUBLIC_SITE}/verify/${code}`;

// A certificate number in a sentence must not break at its hyphens ("DRS-2026-" / "000001"): the
// non-breaking hyphen (U+2011) keeps it on one line in a narrow dialog title.
const unbreakable = (number) => String(number ?? '').replace(/-/g, '\u2011');

function pickName(text, lang) {
  if (!text) return '';
  return text[lang] || text.uz || text.en || '';
}

// While the design and the authors load, the page holds its own shape: three tiles, the design
// card with its thumbnails, the authors card — not a grid of product cards.
function SettingsSkeleton({ label }) {
  return (
    <div className="ui-stack cert-skel" role="status" aria-live="polite" aria-busy="true" aria-label={label}>
      <div className="kpi-row cert-kpis" aria-hidden="true">
        <MetricCard loading />
        <MetricCard loading />
        <MetricCard loading />
      </div>
      <section className="section-card cert-card" aria-hidden="true">
        <div className="cert-skel-head">
          <div className="ui-skel cert-skel-title" />
          <div className="ui-skel cert-skel-line" />
        </div>
        <div className="cert-designs">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="ui-skel cert-skel-thumb" />
          ))}
        </div>
      </section>
      <section className="section-card cert-card" aria-hidden="true">
        <div className="cert-skel-head">
          <div className="ui-skel cert-skel-title" />
          <div className="ui-skel cert-skel-line" />
        </div>
        <div className="ui-skel cert-skel-block" />
      </section>
      <span className="visually-hidden">{label}</span>
    </div>
  );
}

// ── Design picker ────────────────────────────────────────────────────────────

function DesignPicker({ courseId, templates, settings, onSaved }) {
  const { t, lang } = useI18n();
  const [selected, setSelected] = useState(settings?.templateId || null);
  const [saving, setSaving] = useState(false);
  const [sampleBusy, setSampleBusy] = useState(null);

  useEffect(() => {
    setSelected(settings?.templateId || null);
  }, [settings?.templateId]);

  const openSample = async (templateId) => {
    // Open the tab synchronously so popup blockers allow it, then point it at the PDF.
    const tab = window.open('', '_blank');
    setSampleBusy(templateId);
    try {
      const blob = await getCourseCertificateSample(courseId, templateId);
      const url = URL.createObjectURL(blob);
      if (tab) tab.location.href = url;
      else window.location.assign(url);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      if (tab) tab.close();
      toast.error(t('certs.sampleFailed'));
    } finally {
      setSampleBusy(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const next = await updateCourseCertificateSettings(courseId, { templateId: selected });
      onSaved(next);
      toast.success(t('certs.designSaved'));
    } catch {
      toast.error(t('certs.designSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const dirty = !!settings && selected !== settings.templateId;

  return (
    <section className="section-card cert-card">
      <div className="section-head cert-head">
        <div>
          <h2 className="section-head__title">{t('certs.designTitle')}</h2>
          <p className="section-head__desc">
            {t('certs.designSub')}{' '}
            {settings && settings.issuedCount > 0
              ? t('certs.designNoteIssued', { count: settings.issuedCount })
              : t('certs.designNoteNone')}
          </p>
        </div>
        <button
          type="button"
          className={`ds-btn ${dirty ? 'ds-btn-primary' : 'ds-btn-secondary'}${saving ? ' is-loading' : ''}`}
          onClick={save}
          disabled={!dirty || saving}
        >
          <span>{saving ? t('certs.saving') : t('certs.saveDesign')}</span>
        </button>
      </div>

      <div className="cert-designs" role="radiogroup" aria-label={t('certs.designTitle')}>
        {templates.map((tpl) => {
          const active = selected === tpl.id;
          const current = settings?.templateId === tpl.id;
          const preview = certificateAssetUrl(tpl.previewPath);
          const name = pickName(tpl.name, lang);
          return (
            <div key={tpl.id} className="cert-design" data-active={active ? 'true' : undefined}>
              <button
                type="button"
                role="radio"
                aria-checked={active}
                aria-label={name}
                className="cert-design-pick"
                onClick={() => setSelected(tpl.id)}
              >
                {preview ? (
                  <img src={preview} alt="" loading="lazy" />
                ) : (
                  <span className="cert-design-ph" />
                )}
                <span className="cert-design-check" aria-hidden="true"><Check /></span>
              </button>
              <div className="cert-design-body">
                <div className="cert-design-meta">
                  <span className="cert-design-name">{name}</span>
                  {current && <span className="ds-badge ds-badge-brand">{t('certs.current')}</span>}
                  {/* "Asosiy" is a fact about the design, "Joriy" is this course's choice: a plain word, not a second pill */}
                  {tpl.isDefault && <span className="cert-design-default">{t('certs.default')}</span>}
                </div>
                <p className="cert-design-desc">{pickName(tpl.description, lang)}</p>
                <button
                  type="button"
                  className="ds-btn ds-btn-ghost ds-btn-sm cert-design-sample"
                  onClick={() => openSample(tpl.id)}
                  disabled={sampleBusy === tpl.id}
                  title={t('certs.samplePdfHint')}
                >
                  <FileText aria-hidden="true" />
                  <span>{t('certs.samplePdf')}</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── Authors editor ───────────────────────────────────────────────────────────

function toRows(settings) {
  return (settings?.authors || []).map((a) => ({ name: a.name, title: a.title, titleEn: a.titleEn || '' }));
}

function rowErrors(row) {
  const e = {};
  const n = row.name.trim().length;
  const tl = row.title.trim().length;
  if (n < 2 || n > 80) e.name = true;
  if (tl < 2 || tl > 120) e.title = true;
  if (row.titleEn.trim().length > 120) e.titleEn = true;
  return e;
}

function AuthorsEditor({ courseId, settings, onSaved }) {
  const { t } = useI18n();
  const [rows, setRows] = useState(() => toRows(settings));
  const [titleEn, setTitleEn] = useState(settings?.certificateTitleEn || '');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRows(toRows(settings));
    setTitleEn(settings?.certificateTitleEn || '');
    setTouched(false);
  }, [settings]);

  const dirty = useMemo(() => {
    const base = JSON.stringify({ r: toRows(settings), e: settings?.certificateTitleEn || '' });
    return JSON.stringify({ r: rows, e: titleEn }) !== base;
  }, [rows, titleEn, settings]);

  const errors = rows.map(rowErrors);
  const invalid = errors.some((e) => Object.keys(e).length > 0) || titleEn.trim().length > 300;

  const update = (i, field, value) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  const move = (i, dir) =>
    setRows((prev) => {
      const next = [...prev];
      const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const save = async () => {
    setTouched(true);
    if (invalid) return;
    setSaving(true);
    try {
      const next = await updateCourseCertificateSettings(courseId, {
        authors: rows.map((r) => ({
          name: r.name.trim(),
          title: r.title.trim(),
          ...(r.titleEn.trim() ? { titleEn: r.titleEn.trim() } : {}),
        })),
        certificateTitleEn: titleEn.trim() || null,
      });
      onSaved(next);
      toast.success(t('certs.authorsSaved'));
    } catch {
      toast.error(t('certs.authorsSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="section-card cert-card">
      <div className="section-head cert-head">
        <div>
          <h2 className="section-head__title">{t('certs.authorsTitle')}</h2>
          <p className="section-head__desc">{t('certs.authorsSub')}</p>
        </div>
        <div className="cert-head-actions">
          {dirty && <span className="ds-badge ds-badge-warning">{t('certs.unsaved')}</span>}
          <button
            type="button"
            className={`ds-btn ${dirty ? 'ds-btn-primary' : 'ds-btn-secondary'}${saving ? ' is-loading' : ''}`}
            onClick={save}
            disabled={!dirty || saving}
          >
            <span>{saving ? t('certs.saving') : t('certs.saveAuthors')}</span>
          </button>
        </div>
      </div>

      {rows.length === 0 && <p className="ui-hint">{t('certs.authorsEmpty')}</p>}
      {rows.length > 0 && (
        <ol className="cert-authors">
          {rows.map((row, i) => {
            const e = touched ? errors[i] : {};
            return (
              <li key={i} className="section-card section-card--sunken cert-author">
                <span className="ui-avatar ui-avatar--sm cert-author-num" aria-hidden="true">{i + 1}</span>
                <div className="cert-author-fields">
                  <div className="ui-field">
                    <label className="ui-label" htmlFor={`cert-author-name-${i}`}>{t('certs.authorName')}</label>
                    <input
                      id={`cert-author-name-${i}`}
                      type="text"
                      value={row.name}
                      maxLength={80}
                      placeholder={t('certs.authorNamePh')}
                      aria-invalid={e.name ? 'true' : undefined}
                      onChange={(ev) => update(i, 'name', ev.target.value)}
                    />
                    {e.name && <p className="ui-error">{t('certs.errName')}</p>}
                  </div>
                  <div className="ui-field">
                    <label className="ui-label" htmlFor={`cert-author-title-${i}`}>{t('certs.authorTitle')}</label>
                    <input
                      id={`cert-author-title-${i}`}
                      type="text"
                      value={row.title}
                      maxLength={120}
                      placeholder={t('certs.authorTitlePh')}
                      aria-invalid={e.title ? 'true' : undefined}
                      onChange={(ev) => update(i, 'title', ev.target.value)}
                    />
                    {e.title && <p className="ui-error">{t('certs.errTitle')}</p>}
                  </div>
                  <div className="ui-field">
                    <label className="ui-label" htmlFor={`cert-author-title-en-${i}`}>{t('certs.authorTitleEn')}</label>
                    <input
                      id={`cert-author-title-en-${i}`}
                      type="text"
                      value={row.titleEn}
                      maxLength={120}
                      placeholder={t('certs.authorTitleEnPh')}
                      aria-invalid={e.titleEn ? 'true' : undefined}
                      onChange={(ev) => update(i, 'titleEn', ev.target.value)}
                    />
                    {e.titleEn && <p className="ui-error">{t('certs.errTitleEn')}</p>}
                  </div>
                </div>
                <div className="cert-author-tools">
                  <button type="button" className="ui-icon-btn" onClick={() => move(i, -1)} disabled={i === 0} aria-label={t('certs.moveUp')} title={t('certs.moveUp')}>
                    <ArrowUp aria-hidden="true" />
                  </button>
                  <button type="button" className="ui-icon-btn" onClick={() => move(i, 1)} disabled={i === rows.length - 1} aria-label={t('certs.moveDown')} title={t('certs.moveDown')}>
                    <ArrowDown aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="ui-icon-btn ui-icon-btn--danger"
                    onClick={() => setRows((prev) => prev.filter((_, idx) => idx !== i))}
                    aria-label={t('certs.removeAuthor')}
                    title={t('certs.removeAuthor')}
                  >
                    <Trash2 aria-hidden="true" />
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {rows.length < MAX_AUTHORS && (
        <button
          type="button"
          className="ds-btn ds-btn-secondary ds-btn-sm cert-add"
          onClick={() => setRows((prev) => [...prev, { name: '', title: '', titleEn: '' }])}
        >
          <Plus aria-hidden="true" />
          {t('certs.addAuthor')}
        </button>
      )}

      <div className="ui-field cert-title-en">
        <label className="ui-label" htmlFor="cert-title-en">{t('certs.titleEnLabel')}</label>
        <input
          id="cert-title-en"
          type="text"
          value={titleEn}
          maxLength={300}
          placeholder={t('certs.titleEnPh')}
          onChange={(ev) => setTitleEn(ev.target.value)}
        />
        <p className="ui-hint">{t('certs.titleEnHint')}</p>
      </div>
    </section>
  );
}

// ── Revoke / re-issue dialogs ────────────────────────────────────────────────

function ActionDialog({ kind, row, onClose, onDone }) {
  const { t } = useI18n();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const isRevoke = kind === 'revoke';
  const valid = isRevoke ? text.trim().length >= 3 && text.trim().length <= 300 : text.trim().length <= 80;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      if (isRevoke) await revokeCertificate(row.certificateId, text.trim());
      else await reissueCertificate(row.certificateId, text.trim() || undefined);
      onDone(isRevoke ? t('certs.revoked') : t('certs.reissued'));
    } catch {
      setError(isRevoke ? t('certs.revokeFailed') : t('certs.reissueFailed'));
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => { if (!busy) onClose(); }}
      title={t(isRevoke ? 'certs.revokeTitle' : 'certs.reissueTitle', { number: unbreakable(row.certificateNumber) })}
      size="md"
      footer={(
        <>
          <button type="button" className="ds-btn ds-btn-secondary" onClick={onClose} disabled={busy}>
            {t('certs.cancel')}
          </button>
          <button
            type="button"
            className={`ds-btn ${isRevoke ? 'ds-btn-danger-fill' : 'ds-btn-primary'}${busy ? ' is-loading' : ''}`}
            onClick={submit}
            disabled={!valid || busy}
          >
            <span>{t(isRevoke ? 'certs.confirmRevoke' : 'certs.confirmReissue')}</span>
          </button>
        </>
      )}
    >
      <div className="cert-dialog-body">
        <p>{t(isRevoke ? 'certs.revokeBody' : 'certs.reissueBody')}</p>
        <div className="ui-field">
          <label className="ui-label" htmlFor="cert-dialog-text">{t(isRevoke ? 'certs.reasonLabel' : 'certs.nameLabel')}</label>
          {isRevoke ? (
            <textarea id="cert-dialog-text" rows={3} maxLength={300} value={text} onChange={(e) => setText(e.target.value)} data-autofocus />
          ) : (
            <input id="cert-dialog-text" type="text" maxLength={80} value={text} onChange={(e) => setText(e.target.value)} data-autofocus />
          )}
          <p className="ui-hint">
            {isRevoke ? t('certs.reasonHint') : t('certs.nameHint', { name: row.studentName })}
          </p>
        </div>
        {error && (
          <div className="ui-alert" role="alert">
            <CircleAlert aria-hidden="true" />
            <p>{error}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Students table ───────────────────────────────────────────────────────────

function StudentsTable({ courseId, onChanged }) {
  const { t } = useI18n();
  const { canIssueCertificate, canRevokeCertificate } = usePermissions();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState({});
  const [copied, setCopied] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulk, setBulk] = useState(null);
  const [dialog, setDialog] = useState(null);
  const copiedTimer = useRef(null);
  const pagination = usePagination({ limit: 20 });
  const { page, limit, applyMeta } = pagination;

  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getCourseCertificates(courseId, { page, limit });
      setRows(unwrapList(result));
      applyMeta(unwrapMeta(result, { limit }));
      setSelected(new Set());
      setError(null);
    } catch (err) {
      setError(err.code === 403 ? t('certs.accessDenied') : t('certs.loadError'));
    } finally {
      setLoading(false);
    }
  }, [courseId, page, limit, applyMeta, t]);

  useEffect(() => {
    load();
  }, [load]);

  const ready = rows.filter((r) => r.isComplete && !r.certificateGenerated);

  const issue = async (enrollmentId) => {
    setBusy((b) => ({ ...b, [enrollmentId]: true }));
    try {
      await generateCourseCertificate(courseId, enrollmentId);
      toast.success(t('certs.issued'));
      await load();
      onChanged();
    } catch {
      toast.error(t('certs.issueFailed'));
    } finally {
      setBusy((b) => {
        const next = { ...b };
        delete next[enrollmentId];
        return next;
      });
    }
  };

  const issueSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBulk({ done: 0, total: ids.length });
    let succeeded = 0;
    for (const id of ids) {
      try {
        await generateCourseCertificate(courseId, id);
        succeeded += 1;
      } catch (_) {
        // keep going — the summary reports how many went through
      }
      setBulk((p) => ({ ...p, done: p.done + 1 }));
    }
    setBulk(null);
    // Every one went through: good news; some did not: say how many did, as a problem
    (succeeded === ids.length ? toast.success : toast.error)(t('certs.bulkDone', { succeeded, total: ids.length }));
    await load();
    onChanged();
  };

  const copyLink = async (row) => {
    try {
      let url = row.verifyCode ? verifyUrl(row.verifyCode) : null;
      if (!url) {
        const { publicUrl, shareLink } = await getCertificateShareLink(row.certificateId);
        url = publicUrl || shareLink;
      }
      await navigator.clipboard.writeText(url);
      setCopied(row.certificateId);
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error(t('certs.copyFailed'));
    }
  };

  const statusOf = (row) => {
    if (row.status === 'REVOKED') return { tone: 'danger', label: t('certs.statusRevoked') };
    if (row.status === 'ISSUED' || row.certificateGenerated) return { tone: 'success', label: t('certs.statusIssued') };
    if (row.isComplete) return { tone: 'warning', label: t('certs.statusReady') };
    return { tone: 'info', label: t('certs.statusInProgress') };
  };

  const allReadySelected = selected.size > 0 && selected.size === ready.length;

  let body;
  if (loading && rows.length === 0) {
    body = <LoadingState skeleton="table" rows={5} />;
  } else if (error && rows.length === 0) {
    body = (
      <PageEmptyState
        variant={error === t('certs.accessDenied') ? 'forbidden' : 'error'}
        description={error}
        onRetry={error === t('certs.accessDenied') ? undefined : load}
      />
    );
  } else if (rows.length === 0) {
    body = <PageEmptyState variant="students" title={t('certs.studentsEmptyTitle')} description={t('certs.studentsEmptyDesc')} />;
  } else {
    body = (
      <TableCard
        className="cert-table-card"
        tall={rows.length > TALL_ROWS}
        aria-label={t('certs.studentsTitle')}
        footer={(
          <div className="cert-foot">
            <span className="cert-count">{t('certs.studentsCount', { count: formatNumber(pagination.total, { decimals: 0 }) })}</span>
            <Pagination state={pagination} hideOnSinglePage />
          </div>
        )}
      >
        <table className="ui-table ui-table--stack cert-table">
          <thead>
            <tr>
              <th>{t('certs.colStudent')}</th>
              <th>{t('certs.colProgress')}</th>
              <th>{t('certs.colCompleted')}</th>
              <th>{t('certs.colCertificate')}</th>
              <th>{t('certs.colStatus')}</th>
              <th className="cert-col-actions"><span className="visually-hidden">{t('certs.colActions')}</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const st = statusOf(row);
              const isRevoked = row.status === 'REVOKED';
              const hasCert = !!row.certificateId && !!row.certificateNumber;
              const canIssueRow = row.isComplete && !row.certificateGenerated && !isRevoked;
              const pdf = publicCertificatePdfUrl(row.verifyCode);
              const progress = Math.max(0, Math.min(100, Number(row.progressPercent ?? 0)));
              // The less common actions live behind the "⋯" button; the rest are inline. (Text only:
              // an icon-led item would take the menu's stretched first-span rule for its label.)
              const menuItems = [];
              if (hasCert && row.verifyCode) {
                menuItems.push({
                  key: 'verify',
                  label: t('certs.actionVerify'),
                  onSelect: () => window.open(verifyUrl(row.verifyCode), '_blank', 'noopener,noreferrer'),
                });
              }
              if (hasCert && canRevokeCertificate()) {
                menuItems.push({
                  key: 'reissue',
                  label: t('certs.actionReissue'),
                  onSelect: () => setDialog({ kind: 'reissue', row }),
                });
              }
              if (hasCert && canRevokeCertificate() && !isRevoked) {
                menuItems.push({
                  key: 'revoke',
                  label: t('certs.actionRevoke'),
                  tone: 'danger',
                  onSelect: () => setDialog({ kind: 'revoke', row }),
                });
              }
              return (
                <tr key={row.enrollmentId}>
                  <td>
                    <div className="cert-student">
                      {canIssueRow && canIssueCertificate() ? (
                        <label className="cert-check">
                          <input
                            type="checkbox"
                            aria-label={row.studentName}
                            checked={selected.has(row.enrollmentId)}
                            disabled={!!bulk}
                            onChange={() =>
                              setSelected((prev) => {
                                const next = new Set(prev);
                                if (next.has(row.enrollmentId)) next.delete(row.enrollmentId);
                                else next.add(row.enrollmentId);
                                return next;
                              })
                            }
                          />
                        </label>
                      ) : (
                        ready.length > 0 && <span className="cert-check" aria-hidden="true" />
                      )}
                      <div className="ui-cell-person">
                        <Avatar name={row.studentName} id={row.userId || row.enrollmentId} />
                        <div className="ui-cell-person__text">
                          <span className="ui-cell-person__name">{row.studentName}</span>
                          <span className="ui-cell-person__sub">{row.studentEmail}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td data-label={t('certs.colProgress')}>
                    <div className={`cert-progress${progress >= 100 ? ' is-complete' : ''}`}>
                      <ProgressBar className="cert-bar" value={progress} label={t('certs.colProgress')} />
                      <span className="cert-pct">{formatPercent(progress, { decimals: 0 })}</span>
                    </div>
                  </td>
                  <td className="cert-nowrap cert-muted" data-label={t('certs.colCompleted')}>
                    {row.completionDate ? formatDate(row.completionDate) : '—'}
                  </td>
                  <td className="cert-nowrap" data-label={t('certs.colCertificate')}>
                    {row.certificateNumber ? (
                      <span className="cert-number-wrap">
                        <span className="cert-number">{row.certificateNumber}</span>
                        {row.withDistinction && (
                          <Star
                            className="cert-star"
                            role="img"
                            aria-label={t('certs.distinction')}
                          />
                        )}
                      </span>
                    ) : '—'}
                  </td>
                  <td data-label={t('certs.colStatus')}>
                    <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
                  </td>
                  <td className="cert-col-actions ui-row-actions-cell">
                    <div className="ui-row-actions">
                      {canIssueRow && canIssueCertificate() && (
                        <button
                          type="button"
                          className="ds-btn ds-btn-secondary ds-btn-sm"
                          onClick={() => issue(row.enrollmentId)}
                          disabled={!!busy[row.enrollmentId] || !!bulk}
                        >
                          <Award aria-hidden="true" />
                          {busy[row.enrollmentId] ? t('certs.actionIssuing') : t('certs.actionIssue')}
                        </button>
                      )}
                      {hasCert && pdf && (
                        <a
                          href={pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ui-icon-btn"
                          aria-label={t('certs.actionOpenPdf')}
                          title={t('certs.actionOpenPdf')}
                        >
                          <FileText aria-hidden="true" />
                        </a>
                      )}
                      {hasCert && !isRevoked && (
                        <button
                          type="button"
                          className="ui-icon-btn"
                          onClick={() => copyLink(row)}
                          aria-label={copied === row.certificateId ? t('certs.copied') : t('certs.actionCopyLink')}
                          title={copied === row.certificateId ? t('certs.copied') : t('certs.actionCopyLink')}
                        >
                          {copied === row.certificateId ? <Check aria-hidden="true" /> : <Link2 aria-hidden="true" />}
                        </button>
                      )}
                      {menuItems.length > 0 && <ActionMenu ariaLabel={t('certs.moreActions')} items={menuItems} />}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableCard>
    );
  }

  return (
    <section className="cert-students">
      <div className="section-head cert-head">
        <div>
          <h2 className="section-head__title">{t('certs.studentsTitle')}</h2>
          <p className="section-head__desc">{t('certs.studentsSub')}</p>
        </div>
        {canIssueCertificate() && ready.length > 0 && (
          <div className="cert-head-actions">
            <label className="ui-check">
              <input
                type="checkbox"
                checked={allReadySelected}
                onChange={() =>
                  setSelected(allReadySelected ? new Set() : new Set(ready.map((r) => r.enrollmentId)))
                }
              />
              {t('certs.selectAllReady', { count: ready.length })}
            </label>
            <button
              type="button"
              className="ds-btn ds-btn-secondary ds-btn-sm"
              disabled={!selected.size || !!bulk}
              onClick={issueSelected}
            >
              {bulk ? t('certs.issuingProgress', bulk) : t('certs.issueSelected', { count: selected.size })}
            </button>
          </div>
        )}
      </div>
      <div className="cert-results" aria-busy={loading}>
        {body}
      </div>

      {dialog && (
        <ActionDialog
          kind={dialog.kind}
          row={dialog.row}
          onClose={() => setDialog(null)}
          onDone={async (msg) => {
            setDialog(null);
            toast.success(msg);
            await load();
            onChanged();
          }}
        />
      )}
    </section>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

/**
 * Course certificates workspace: the design students get (with course-specific samples), the
 * authors printed on it, and every student's certificate with issue / PDF / verify / revoke /
 * re-issue. Rendered as the "Sertifikatlar" tab of the course workspace (`embedded`) and at
 * /courses/:courseId/certificates.
 */
function CourseCertificates({ courseId: courseIdProp, embedded = false }) {
  const { t, lang } = useI18n();
  const params = useParams();
  const courseId = courseIdProp || params.courseId;
  // Only the standalone header names the course: inside the workspace the hero above does.
  const courseTitle = useCourseTitle(embedded ? null : courseId);
  const [templates, setTemplates] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      const [tpl, cfg] = await Promise.all([getCertificateTemplates(), getCourseCertificateSettings(courseId)]);
      setTemplates(tpl);
      setSettings(cfg);
      setError(null);
    } catch (err) {
      setError(err.code === 403 || err.code === 404 ? t('certs.accessDenied') : t('certs.loadError'));
    }
  }, [courseId, t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const currentDesign = templates?.find((tpl) => tpl.id === settings?.templateId);

  return (
    <div className="ui-stack cert-page">
      {!embedded && (
        <PageHead
          back={{ to: `/courses/edit/${courseId}?tab=certificates`, label: t('certs.backToCourse') }}
          eyebrow={<span className="cert-eyebrow">{courseTitle || t('certs.parent')}</span>}
          title={t('certs.title')}
          description={t('certs.subtitle')}
        />
      )}

      {error ? (
        <PageEmptyState
          variant={error === t('certs.accessDenied') ? 'forbidden' : 'error'}
          description={error}
          onRetry={error === t('certs.accessDenied') ? undefined : loadSettings}
        />
      ) : !settings || !templates ? (
        <SettingsSkeleton label={t('ui.loading')} />
      ) : (
        <>
          <div className="kpi-row cert-kpis">
            <MetricCard
              title={t('certs.statIssued')}
              value={formatNumber(settings.issuedCount, { decimals: 0 })}
              icon={<Award />}
              tone="brand"
            />
            <MetricCard
              title={t('certs.statRevoked')}
              value={formatNumber(settings.revokedCount, { decimals: 0 })}
              icon={<Ban />}
              tone="coral"
            />
            <MetricCard
              title={t('certs.statDesign')}
              value={currentDesign ? pickName(currentDesign.name, lang) : '—'}
              icon={<Palette />}
              tone="purple"
            />
          </div>
          <DesignPicker courseId={courseId} templates={templates} settings={settings} onSaved={setSettings} />
          <AuthorsEditor courseId={courseId} settings={settings} onSaved={setSettings} />
        </>
      )}

      <StudentsTable courseId={courseId} onChanged={loadSettings} />
    </div>
  );
}

export default CourseCertificates;
